import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import CurrencyFormat from 'react-currency-format';
import cn from '@/utils/cn';
import Breadcrumbs from '@/components/Breadcrumbs';
import { CloseIcon, RoundedCheckIcon, RoundedCloseIcon } from '@/components/Icons';
import Button from '@/components/Button';
import DataTable, { SortDirectionEnum, TTableColumn } from '@/components/DataTable';
import {
  batchModal,
  batchOverviewModal,
  scheduledPaymentModal,
  statementDetails
} from '@/utils/translations';
import { DATE_FORMAT_WEEKDAY_TIME } from '@/utils/dateFormats';
import {
  ScheduledBankPaymentGroupStateEnum,
  ScheduledBankPaymentGroupTypeEnum
} from '@/features/payments/types/BatchPayment';
import { BATCH_LIMIT, TBatchOverviewPayment, TPaymentListItem } from '../types/ManagePayments';
import { PaymentActionVariantEnum } from '../data/paymentActions';
import {
  isBatchAllFailed,
  isBatchIncomplete,
  isPaymentCreator,
  isScheduledWithoutMultiSign
} from '../data/paymentStatus';
import { MultiSignActionAuthorisationStateEnum } from '@/services/types/MultiSign';
import { DESTRUCTIVE_IDLE_CLASS } from './actionButtonClasses';
import useAuth from '@/hooks/useAuth';
import { useCustomerName } from '../hooks/useCustomerName';
import { useBatchOverviewPayments } from '../hooks/useBatchOverviewPayments';
import { useMultiSignApprovers } from '../hooks/useMultiSignApprovers';
import { useScheduledPaymentAction, TPaymentUpdate } from '../hooks/useScheduledPaymentAction';
import { UnableToDeleteReasonEnum } from '../data/unableToDeleteReason';
import RejectPaymentModal from './RejectPaymentModal';
import AlreadyActionedModal from './AlreadyActionedModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import UnableToDeleteModal from './UnableToDeleteModal';
import MarkAsCompleteConfirmModal from './MarkAsCompleteConfirmModal';
import PaymentOutcomeResult, { TPaymentOutcomeVariant } from './PaymentOutcomeResult';
import BatchOverviewHeader from './BatchOverviewHeader';
import LoadingComponent from '@/components/Loading';
import { useWalletBalance } from '../hooks/useWalletBalance';

const TITLE_ID = 'batch-overview-title';

/** 'sticky' pins the action bar to the viewport bottom; 'inline' renders it right after the
 * table, in normal document flow. */
type TActionBarPlacement = 'sticky' | 'inline';

interface BatchOverviewModalProps {
  payment: TPaymentListItem;
  onBack: () => void;
  onClose: () => void;
  onPaymentUpdate?: (update: TPaymentUpdate) => void;
  onPaymentDelete?: (id: string) => void;
  actionBarPlacement?: TActionBarPlacement;
}

type TStatusInfo = { label: string; colorClass: string };

// Shared by every action bar (Reject/Approve, Delete, Mark as complete/Retry) so all three stay
// visually consistent and only one place needs to change if the placement option is extended.
const getActionBarClassName = (placement: TActionBarPlacement) =>
  cn(
    'flex justify-end gap-x-[19px] p-4 bg-white',
    placement === 'sticky' ? 'fixed inset-x-0 bottom-0 border-t border-aux-gray' : 'border-none'
  );

// No dedicated status/colour exists for every possible state - anything not called out in the
// design falls back to the closest already-designed state rather than inventing new copy.
//
// FAILED/COMPLETED_WITH_FAILURES both mean "at least one payment failed" - which of the two the
// backend actually returns can't be trusted to mean all-failed vs some-failed (see
// isBatchAllFailed), so completedPayments decides the label: zero completed reads as "failed"
// (a genuine FAILED state always has zero completed too, so this covers both), otherwise it's a
// real partial failure and keeps the "incomplete" copy.
const getStatusInfo = (
  state: TPaymentListItem['state'],
  completedPayments?: number
): TStatusInfo => {
  switch (state) {
    case ScheduledBankPaymentGroupStateEnum.PENDING_APPROVAL:
      return { label: batchOverviewModal.approvalRequiredStatus, colorClass: 'text-emerge-purple' };
    case ScheduledBankPaymentGroupStateEnum.IN_PROGRESS:
      return { label: batchOverviewModal.inProgressStatus, colorClass: 'text-dark-teal-30' };
    case ScheduledBankPaymentGroupStateEnum.COMPLETED:
      return { label: batchOverviewModal.completedStatus, colorClass: 'text-emerge-teal' };
    case ScheduledBankPaymentGroupStateEnum.FAILED:
    case ScheduledBankPaymentGroupStateEnum.COMPLETED_WITH_FAILURES:
      return isBatchAllFailed(state, completedPayments)
        ? { label: batchOverviewModal.failedStatus, colorClass: 'text-error-darkred' }
        : { label: batchOverviewModal.incompleteStatus, colorClass: 'text-error-darkred' };
    case ScheduledBankPaymentGroupStateEnum.REJECTED:
      return { label: batchOverviewModal.rejectedStatus, colorClass: 'text-error-darkred' };
    case ScheduledBankPaymentGroupStateEnum.EXPIRED:
      return { label: batchOverviewModal.expiredStatus, colorClass: 'text-error-darkred' };
    case ScheduledBankPaymentGroupStateEnum.APPROVED:
    default:
      return { label: batchOverviewModal.approvedStatus, colorClass: 'text-emerge-teal' };
  }
};

/**
 * The full payee-list view behind BatchModal's "View payments"/"View payments to approve"
 * buttons. Reject/Approve is only rendered for PENDING_APPROVAL - every other state (approved,
 * completed, processed but incomplete, rejected, expired) is view-only, reusing the exact
 * multi-sign approve/reject flow already built for individual scheduled payments
 * (useScheduledPaymentAction resolves the same APPROVAL_REQUIRED variant for a batch row, since
 * it keys off `state` alone).
 */
const BatchOverviewModal: React.FC<BatchOverviewModalProps> = ({
  payment,
  onBack,
  onClose,
  onPaymentUpdate,
  onPaymentDelete,
  actionBarPlacement = 'inline'
}) => {
  const actionBarClassName = getActionBarClassName(actionBarPlacement);
  const createdByName = useCustomerName(payment.createdByCustomerId);
  const auth = useAuth();
  // The group's own approverCustomerId isn't reliable once a rule requires more than one
  // sign-off - the authoritative list is the multi-sign action's full authorisations, same
  // reasoning ScheduledPaymentModal already follows for individual payments. Fetched
  // unconditionally (not gated to "past the approval decision") so hasPersistedApproval below can
  // tell "did I already approve this" from the very first render, while still PENDING_APPROVAL.
  // isLoadingApprovers gates showApprovalActions below - until this authorisation fetch
  // resolves we don't yet know hasPersistedApproval, so showing the buttons early would flash
  // an already-approved payment as actionable (and re-clicking Approve 400s on the backend).
  const {
    approvers,
    hasPersistedApproval,
    hasNoApprovalAuthority,
    isLoading: isLoadingApprovers,
    refetch: refetchApprovers
  } = useMultiSignApprovers(payment.multiSignActionId, true, auth?.customer?.id, payment.state);
  const isInProgress = payment.state === ScheduledBankPaymentGroupStateEnum.IN_PROGRESS;
  const { payments, isLoading: isLoadingPayments } = useBatchOverviewPayments(
    payment.id,
    true,
    isInProgress
  );
  const {
    variant: actionVariant,
    hasApprovedLocally,
    hasRejected,
    hasFinalApproval,
    hasImmediateFailure,
    hasDeleted,
    approve,
    isApproving,
    approveError,
    openDeleteDialog,
    isDeleteDialogOpen,
    closeDeleteDialog,
    confirmDelete,
    isDeleting,
    deleteError,
    unableToDeleteReason,
    dismissUnableToDelete,
    retry,
    isRetrying,
    retryError,
    markAsComplete,
    isMarkAsCompleteDialogOpen,
    openMarkAsCompleteDialog,
    closeMarkAsCompleteDialog,
    isMarkingComplete,
    markAsCompleteError,
    isRejectDialogOpen,
    openRejectDialog,
    closeRejectDialog,
    confirmReject,
    isRejecting,
    rejectError,
    authorisationConflictOutcome,
    dismissAuthorisationConflict
  } = useScheduledPaymentAction(payment, {
    onPaymentUpdate,
    onPaymentDelete,
    hasPersistedApproval,
    hasNoApprovalAuthority,
    refetchApprovers
  });

  // hasDeleted has no dedicated result screen (yet) - the row disappearing from the table already
  // communicates that outcome, same reasoning as ScheduledPaymentModal. hasApprovedLocally and
  // hasFinalApproval both render their own PaymentOutcomeResult instead (see outcomeVariant
  // below).
  useEffect(() => {
    if (hasDeleted) onClose();
  }, [hasDeleted, onClose]);

  // Tracks a live IN_PROGRESS -> COMPLETED/FAILED transition while this modal stays open (driven
  // by the background poll in useBatchPayment) so it can show the matching full-screen result.
  // Keyed by payment.id too so switching to a different payment never carries over a stale
  // outcome or misreads its initial state as a "transition". COMPLETED_WITH_FAILURES has no
  // dedicated result screen - it falls through to the normal incomplete-state view instead.
  const prevPaymentRef = useRef({ id: payment.id, state: payment.state });
  const [processingOutcome, setProcessingOutcome] = useState<
    'batchComplete' | 'batchFailed' | null
  >(null);
  useEffect(() => {
    const prev = prevPaymentRef.current;
    prevPaymentRef.current = { id: payment.id, state: payment.state };

    if (prev.id !== payment.id) {
      setProcessingOutcome(null);
      return;
    }
    if (prev.state !== ScheduledBankPaymentGroupStateEnum.IN_PROGRESS) return;

    if (payment.state === ScheduledBankPaymentGroupStateEnum.COMPLETED) {
      setProcessingOutcome('batchComplete');
    } else if (payment.state === ScheduledBankPaymentGroupStateEnum.FAILED) {
      setProcessingOutcome('batchFailed');
    }
  }, [payment.id, payment.state]);

  // Xero batches never get an altering action here either - they're managed from Xero, not this
  // app - so Reject/Approve/Delete all stay hidden even if the row's state would otherwise call
  // for one of them.
  const isXero = payment.type === ScheduledBankPaymentGroupTypeEnum.XERO;
  const showApprovalActions =
    actionVariant === PaymentActionVariantEnum.APPROVAL_REQUIRED && !isXero && !isLoadingApprovers;
  const showLoadingApprovalActions =
    actionVariant === PaymentActionVariantEnum.APPROVAL_REQUIRED && !isXero && isLoadingApprovers;
  // Delete is scoped to the batch's own creator - see isPaymentCreator - not every admin who can
  // otherwise reach this variant. NO_APPROVAL_AUTHORITY must NOT grant delete: a batch still
  // pending approval that the current user isn't a signatory on is only deletable once it reaches
  // APPROVED - a non-authorising creator deleting it out of Action Required would sidestep
  // multi-sign approval entirely.
  const showDeleteAction =
    actionVariant === PaymentActionVariantEnum.DELETE &&
    !isXero &&
    isPaymentCreator(payment.createdByCustomerId, auth?.customer?.id);
  // Overrides whatever getStatusInfo would otherwise say (e.g. "processed but incomplete") -
  // force-completing is a distinct, terminal outcome of its own. NO_APPROVAL_AUTHORITY is checked
  // next - the current user isn't a signatory on this action at all, so "Batch payment approval
  // required" would wrongly suggest they have something to do here. An APPROVED batch with no
  // multiSignActionId never actually went through anyone's approval - it was simply scheduled.
  const statusInfo = payment.forceCompleted
    ? { label: batchOverviewModal.markedAsCompleteStatus, colorClass: 'text-error-darkred' }
    : actionVariant === PaymentActionVariantEnum.NO_APPROVAL_AUTHORITY
      ? { label: batchOverviewModal.noApprovalAuthorityStatus, colorClass: 'text-emerge-purple' }
      : isScheduledWithoutMultiSign(payment.state, payment.multiSignActionId)
        ? { label: batchOverviewModal.scheduledStatus, colorClass: 'text-emerge-teal' }
        : getStatusInfo(payment.state, payment.completedPayments);
  const isIncomplete = isBatchIncomplete(payment.state);
  // Once force-completed, Mark as complete/Retry payments are both retired for good - view-only
  // from here on, same as any other no-action state.
  const showIncompleteActions = isIncomplete && !isXero && !payment.forceCompleted;
  const paidAmount = payments
    .filter((row) => row.lastPaymentState === 'completed')
    .reduce((sum, row) => sum + row.amount, 0);

  // Joined with ", " rather than shown one-per-line, since a multi-sign rule can require more
  // than one approval - there can genuinely be several names on either side.
  const approvedNames = approvers
    .filter((approver) => approver.state === MultiSignActionAuthorisationStateEnum.APPROVED)
    .map((approver) => approver.name);
  const rejectedAuthorisations = approvers.filter(
    (approver) => approver.state === MultiSignActionAuthorisationStateEnum.REJECTED
  );
  const rejectedNames = rejectedAuthorisations.map((approver) => approver.name);
  // payment.rejectionReason (patched in immediately by confirmReject) takes priority - the
  // authorisations list can still be showing the pre-rejection state right after rejecting, since
  // useMultiSignApprovers caches it per multiSignActionId for the session. Falls back to the
  // authorisations list for a batch that was already rejected before this session started.
  const rejectionReason =
    payment.rejectionReason ?? rejectedAuthorisations.find((approver) => approver.notes)?.notes;

  const columns: TTableColumn<TBatchOverviewPayment>[] = [
    {
      key: 'payeeName',
      header: batchOverviewModal.columns.companyPayeeName,
      canSort: true,
      sortAccessor: (row) => row.payeeName,
      cell: (row) => row.payeeName
    },
    {
      key: 'payeeBankAccount',
      header: batchOverviewModal.columns.bankAccountNumber,
      cell: (row) => row.payeeBankAccount
    },
    {
      key: 'amount',
      header: batchOverviewModal.columns.amount,
      cell: (row) => (
        <CurrencyFormat
          value={Number(row.amount).toFixed(2)}
          displayType="text"
          thousandSeparator
          prefix="$"
          decimalScale={2}
        />
      )
    },
    {
      key: 'payeeParticulars',
      header: batchOverviewModal.columns.particulars,
      cell: (row) => row.payeeParticulars || statementDetails.noParticulars
    },
    {
      key: 'payeeAnalysis',
      header: batchOverviewModal.columns.code,
      cell: (row) => row.payeeAnalysis || statementDetails.noCode
    },
    {
      key: 'payeeReference',
      header: batchOverviewModal.columns.reference,
      cell: (row) => row.payeeReference || statementDetails.noReference
    },
    {
      key: 'paymentDate',
      header: batchOverviewModal.columns.paymentDate,
      cell: (row) => (
        <div className="flex items-center gap-x-1">
          <span className={row.lastPaymentState === 'failed' ? 'text-error-darkred' : undefined}>
            {moment(row.paymentDate).format(DATE_FORMAT_WEEKDAY_TIME)}
          </span>
          {row.lastPaymentState === 'completed' && <RoundedCheckIcon size="xs" />}
          {row.lastPaymentState === 'failed' && <RoundedCloseIcon size="xs" />}
        </div>
      )
    }
  ];

  // hasImmediateFailure checked before hasFinalApproval - both are set from the very same final
  // approval (see useScheduledPaymentAction's post-approval group-status fetch for a batch), only
  // one of the two ever ends up true. Reuses the existing 'batchFailed' copy (generic - the group
  // status fetch doesn't give a specific reason, unlike an individual scheduled payment's 4017)
  // rather than the payment-specific insufficient-funds wording. hasApprovedLocally (other
  // approvers still required) checked last among the "just acted this session" flags - mirrors
  // ScheduledPaymentModal's equivalent ordering.
  const baseOutcomeVariant: TPaymentOutcomeVariant | null = hasRejected
    ? 'batchRejected'
    : hasImmediateFailure
      ? 'batchFailed'
      : hasFinalApproval
        ? 'batchScheduled'
        : hasApprovedLocally
          ? 'batchApproved'
          : processingOutcome;

  // A failed batch - whether the final approval's immediate run failed or a processing run failed
  // while this stayed open - is checked against the pay-from account's live balance, same
  // inference as BatchModal's banner and the batch submission result. Held on a loader until the
  // balance resolves so the generic failure never flips to insufficient funds on screen. Unknown
  // balance (failed fetch) keeps the generic failure.
  const isFailedOutcome = baseOutcomeVariant === 'batchFailed';
  const { balance: walletBalance, isLoading: isLoadingWalletBalance } = useWalletBalance(
    payment.walletId,
    isFailedOutcome
  );
  const isInsufficientFunds =
    isFailedOutcome && walletBalance !== null && walletBalance < Number(payment.totalAmount);
  const outcomeVariant: TPaymentOutcomeVariant | null = isInsufficientFunds
    ? 'batchInsufficientFunds'
    : baseOutcomeVariant;

  if (outcomeVariant) {
    return (
      <div
        className="fixed inset-0 z-99 overflow-y-auto bg-aux-lightgray p-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}>
        <button
          type="button"
          onClick={onClose}
          aria-label={scheduledPaymentModal.closeLabel}
          className="absolute right-6 top-6 flex h-[38px] w-[38px] items-center justify-center rounded-full border border-aux-gray bg-white hover:bg-emerge-base-3">
          <CloseIcon onClick={onClose} size="sm" />
        </button>

        <div className="mx-auto flex min-h-full w-[430px] max-w-full flex-col justify-center">
          {isFailedOutcome && isLoadingWalletBalance ? (
            <div
              role="status"
              aria-label={batchOverviewModal.loadingOutcome}
              className="flex justify-center rounded-2xl border border-aux-gray bg-white py-24">
              <LoadingComponent />
            </div>
          ) : (
            <PaymentOutcomeResult
              variant={outcomeVariant}
              batchName={payment.name}
              accountName={payment.payFromAccount?.name}
              onClose={onClose}
              titleId={TITLE_ID}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-99 overflow-y-auto bg-white"
      role="dialog"
      aria-modal="true"
      aria-labelledby={TITLE_ID}>
      <button
        type="button"
        onClick={onClose}
        aria-label={scheduledPaymentModal.closeLabel}
        className="absolute right-6 top-6 flex h-[38px] w-[38px] items-center justify-center rounded-full border border-aux-gray bg-white hover:bg-emerge-base-3">
        <CloseIcon onClick={onClose} size="sm" />
      </button>

      <Breadcrumbs
        variant="back"
        onBack={onBack}
        paths={[{ label: batchOverviewModal.backLinkLabel, path: undefined }]}
      />

      <div
        className={cn(
          'flex flex-col gap-t-6 pt-16 bg-aux-lightgray',
          // Only the sticky bar needs clearance so it doesn't overlap the last section on scroll.
          actionBarPlacement === 'sticky' ? 'pb-24' : ''
        )}>
        <BatchOverviewHeader
          titleId={TITLE_ID}
          payment={payment}
          createdByName={createdByName}
          statusInfo={statusInfo}
          isIncomplete={isIncomplete}
          isLoadingPayments={isLoadingPayments}
          paidAmount={paidAmount}
          approvedNames={approvedNames}
          rejectedNames={rejectedNames}
          rejectionReason={rejectionReason}
        />

        <div className="bg-white pt-4 px-4">
          <p className="mb-4 text-sm font-medium text-emerge-navy">
            {batchOverviewModal.paymentsSectionLabel}
          </p>
          <DataTable<TBatchOverviewPayment>
            columns={columns}
            data={payments}
            getRowKey={(row) => row.id}
            itemLabel={batchOverviewModal.paymentsSectionLabel}
            isLoading={isLoadingPayments}
            pagination={{ mode: 'client', pageSize: BATCH_LIMIT }}
            sorting={{
              mode: 'client',
              initialSort: { key: 'paymentDate', direction: SortDirectionEnum.ASC }
            }}
          />
        </div>

        {showLoadingApprovalActions && (
          <p role="status" className="text-xs font-medium text-aux-gray">
            {scheduledPaymentModal.loadingApprovers}
          </p>
        )}

        {showApprovalActions && (
          <div className={actionBarClassName}>
            <Button
              variant="primary_inverted"
              size="pill"
              onClick={openRejectDialog}
              disabled={isApproving}
              className={cn(DESTRUCTIVE_IDLE_CLASS, 'w-[142px]')}>
              {scheduledPaymentModal.rejectButton}
            </Button>
            <Button size="pill" onClick={approve} disabled={isApproving} className="w-[142px]">
              {isApproving
                ? scheduledPaymentModal.approvingLabel
                : scheduledPaymentModal.approveButton}
            </Button>
          </div>
        )}

        {showDeleteAction && (
          <div className={actionBarClassName}>
            <Button
              size="pill"
              onClick={openDeleteDialog}
              disabled={isDeleting}
              className="w-[142px] px-9 py-4 bg-aux-lightred text-error-darkred hover:bg-aux-lightred whitespace-nowrap">
              {isDeleting
                ? scheduledPaymentModal.deletingButton
                : batchOverviewModal.deleteBatchButton}
            </Button>
          </div>
        )}

        {showIncompleteActions && (
          <>
            <div className={actionBarClassName}>
              <Button
                variant="primary_inverted"
                size="pill"
                onClick={openMarkAsCompleteDialog}
                disabled={isMarkingComplete || isRetrying}
                className="w-[142px]">
                {isMarkingComplete
                  ? batchOverviewModal.markingAsCompleteButton
                  : batchOverviewModal.markAsCompleteButton}
              </Button>
              <Button
                size="pill"
                onClick={retry}
                disabled={isMarkingComplete || isRetrying}
                className="w-[142px]">
                {isRetrying ? scheduledPaymentModal.retryingButton : batchModal.retryPaymentsButton}
              </Button>
            </div>
            {retryError && (
              <p role="alert" className="text-xs font-medium text-error-darkred">
                {retryError}
              </p>
            )}
          </>
        )}

        {approveError && showApprovalActions && (
          <p role="alert" className="text-xs font-medium text-error-darkred">
            {approveError}
          </p>
        )}
      </div>

      <RejectPaymentModal
        isOpen={isRejectDialogOpen}
        onClose={closeRejectDialog}
        onConfirm={confirmReject}
        isSubmitting={isRejecting}
        error={rejectError}
        isBatch
      />

      <MarkAsCompleteConfirmModal
        isOpen={isMarkAsCompleteDialogOpen}
        onClose={closeMarkAsCompleteDialog}
        onConfirm={markAsComplete}
        isSubmitting={isMarkingComplete}
        error={markAsCompleteError}
      />

      <AlreadyActionedModal
        isOpen={!!authorisationConflictOutcome}
        outcome={authorisationConflictOutcome}
        isBatch
        onClose={() => {
          dismissAuthorisationConflict();
          onClose();
        }}
      />

      <DeleteConfirmModal
        isOpen={isDeleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
        isSubmitting={isDeleting}
        error={deleteError}
        headerText={batchOverviewModal.deleteConfirmTitle}
        bodyText={batchOverviewModal.deleteConfirmBody(payment.name)}
      />

      <UnableToDeleteModal
        isOpen={!!unableToDeleteReason}
        onClose={dismissUnableToDelete}
        headerText={batchOverviewModal.unableToDeleteTitle}
        bodyText={
          unableToDeleteReason === UnableToDeleteReasonEnum.WITHIN_BLOCK_WINDOW
            ? batchOverviewModal.deleteBlockedBody
            : batchOverviewModal.deleteConflictBody
        }
        buttonText={batchOverviewModal.viewBatchButton}
      />
    </div>
  );
};

export default BatchOverviewModal;
